import React, { useState } from 'react';
import { IconCheck, IconCopy, IconPencil, IconPlus, IconRotate } from '@tabler/icons-react';
import { itemLabel, itemUom } from '../../lib/requestItems';
import { RECEIPT, canCloseForReceipt, closeCopyFor, closeDetail } from '../../lib/shortClose';

function fmtEta(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtWhen(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fallbackCopy(value, done) {
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    done();
  } catch (e) {
    // Clipboard genuinely unavailable - the number stays visible for manual copy.
  }
}

// #288, Session 74z3. The receipt-close control and the state it leaves behind.
//
// Defined at module level rather than inside TrackingRow, per locked rule 2 - a
// component declared in another component's body remounts every render and eats
// input focus. It is a separate component at all because both of TrackingRow's
// return branches need it: a line can be short-closed whether or not it has a
// tracking number, and a vendor cancelling before shipping anything is exactly
// the untracked case.
// Whether ShortCloseControl will render anything at all. The callers need this
// separately so an empty wrapper does not contribute stray margin to a row that
// has no control on it - which is every fully-received line.
function showsShortClose(it, canShortClose) {
  return !!it.receipt_closed || (!!canShortClose && canCloseForReceipt(it));
}

function ShortCloseControl({ it, canShortClose, onShortClose, onReopen }) {
  const copy = closeCopyFor(RECEIPT);
  const detail = closeDetail(it, RECEIPT);

  if (it.receipt_closed) {
    return (
      <div style={{ marginTop: 7 }}>
        <div style={{
          background: '#F7F7F6', border: '1px solid #E8E8E8', borderRadius: 7,
          padding: '8px 11px', fontSize: 11.5, color: '#5C5C5C',
        }}>
          {detail?.byName
            ? <><span style={{ color: '#0F0F0F', fontWeight: 600 }}>{detail.byName}</span> {copy.settledVerb}</>
            : <>This line was {copy.settledVerb}</>}
          {detail?.at ? ' on ' + fmtWhen(detail.at) : ''}
          {detail?.reason && (
            <span style={{ display: 'block', marginTop: 2, fontStyle: 'italic' }}>&ldquo;{detail.reason}&rdquo;</span>
          )}
        </div>
        {canShortClose && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => onReopen(it)}
              style={{
                padding: '6px 10px', background: 'white', color: '#0F0F0F',
                border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11,
                fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
                alignItems: 'center', gap: 4,
              }}
            >
              <IconRotate size={12} /> {copy.reopen}
            </button>
            <span style={{ fontSize: 11, color: '#9A9A9A' }}>Puts the balance back on the outstanding count</span>
          </div>
        )}
      </div>
    );
  }

  if (!canShortClose || !canCloseForReceipt(it)) return null;

  // A ghost button, not a primary one. Closing a line short is a rare,
  // considered act; giving it the weight of "Add tracking" would invite
  // mis-taps on the surface the buyer works every day.
  return (
    <button
      type="button"
      onClick={() => onShortClose(it)}
      style={{
        padding: '6px 10px', background: 'white', color: '#0F0F0F',
        border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11,
        fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      {copy.title}
    </button>
  );
}

// The quantity line. Pulled out of both branches because the short-close
// decision is made from it - "14 received, 6 outstanding" is the fact the buyer
// is acting on, and it was not on this row before #288.
function QtyLine({ it }) {
  const uom = itemUom(it);
  const requested = it.qty_requested != null ? parseFloat(it.qty_requested) : null;
  const received = it.qty_received != null ? parseFloat(it.qty_received) : 0;

  if (it.receipt_closed) {
    const short = requested != null ? requested - received : 0;
    return (
      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 5 }}>
        {requested ?? '—'} {uom} ordered · {received} received
        {short > 0 ? ' · ' + short + ' not coming' : ''}
      </div>
    );
  }

  if (requested != null && received > 0 && received < requested) {
    return (
      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 5 }}>
        {requested} {uom} · <b style={{ color: '#8A5A08' }}>{received} received, {requested - received} outstanding</b>
      </div>
    );
  }

  if (requested != null && received >= requested && requested > 0) {
    return (
      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 5 }}>
        {requested} {uom} · {received} of {requested} received
      </div>
    );
  }

  return <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 5 }}>{requested ?? '—'} {uom}</div>;
}

// One tracking row for a single material_request_items row, as rendered on the Purchasing
// queue. RequestsInboxPage's In Flight mirror deliberately does NOT use this component - it
// renders the same data in its own visual language (Session 74k5, at Colby's direction: the
// two pages' styling is standardized later, not now). The shared piece between them is the
// derivation in lib/requestItems.js, not the presentation.
//
// #288 (74z3) added the receipt-close control here rather than to each page, because this
// component is already rendered by both DetailPanel and MobileDetailSheet - one edit, two
// surfaces, and no way for desktop and mobile to drift apart.
export default function TrackingRow({ it, canAction, onAddTracking, canShortClose, onShortClose, onReopen }) {
  const [copied, setCopied] = useState(false);
  const name = itemLabel(it);
  const uom = itemUom(it);
  const qty = it.qty_requested ?? '—';

  function copyNumber() {
    const value = it.tracking_number || '';
    if (!value) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(done).catch(() => fallbackCopy(value, done));
    } else {
      fallbackCopy(value, done);
    }
  }

  if (it.tracking_number) {
    return (
      <div style={{ borderBottom: '1px solid #F0F0F0', padding: '10px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>{name}</span>
          {it.receipt_closed && (
            <span style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
              borderRadius: 4, padding: '2px 6px', background: '#F3F3F3', color: '#6E6E6E',
              border: '1px solid #E0E0E0', whiteSpace: 'nowrap',
            }}>Closed short</span>
          )}
        </div>
        <QtyLine it={it} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {it.tracking_carrier && (
            <span style={{ fontSize: 10, fontWeight: 700, background: '#E6F1FB', color: '#0C447C', borderRadius: 4, padding: '2px 7px' }}>
              {it.tracking_carrier}
            </span>
          )}
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#0F0F0F', wordBreak: 'break-all' }}>
            {it.tracking_number}
          </span>
          <button
            type="button"
            onClick={copyNumber}
            title="Copy tracking number"
            style={{ border: '1px solid #E0E0E0', background: copied ? '#E1F5EE' : 'white', borderRadius: 6, width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0 }}
          >
            {copied ? <IconCheck size={13} color="#085041" /> : <IconCopy size={13} color="#5C5C5C" />}
          </button>
          {canAction && (
            <button
              type="button"
              onClick={() => onAddTracking(it)}
              title="Edit tracking"
              style={{ border: '1px solid #E0E0E0', background: 'white', borderRadius: 6, width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0 }}
            >
              <IconPencil size={13} color="#5C5C5C" />
            </button>
          )}
          {!it.receipt_closed && (
            <ShortCloseControl it={it} canShortClose={canShortClose} onShortClose={onShortClose} onReopen={onReopen} />
          )}
        </div>
        {it.tracking_eta && (
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 4 }}>ETA {fmtEta(it.tracking_eta)}</div>
        )}
        {it.receipt_closed && (
          <ShortCloseControl it={it} canShortClose={canShortClose} onShortClose={onShortClose} onReopen={onReopen} />
        )}
      </div>
    );
  }

  return (
    <div style={{ borderBottom: '1px solid #F0F0F0', padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>{name}</span>
            {it.receipt_closed && (
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                borderRadius: 4, padding: '2px 6px', background: '#F3F3F3', color: '#6E6E6E',
                border: '1px solid #E0E0E0', whiteSpace: 'nowrap',
              }}>Closed short</span>
            )}
          </div>
          {it.receipt_closed
            ? <QtyLine it={it} />
            : <div style={{ fontSize: 11, color: '#9A9A9A', fontStyle: 'italic' }}>{qty} {uom} · no tracking yet</div>}
        </div>
        {canAction && !it.receipt_closed && (
          <button
            type="button"
            onClick={() => onAddTracking(it)}
            style={{ flexShrink: 0, padding: '6px 10px', background: '#0F0F0F', color: 'white', border: 'none', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <IconPlus size={12} /> Add tracking
          </button>
        )}
      </div>
      {showsShortClose(it, canShortClose) && (
        <div style={{ marginTop: it.receipt_closed ? 0 : 7 }}>
          <ShortCloseControl it={it} canShortClose={canShortClose} onShortClose={onShortClose} onReopen={onReopen} />
        </div>
      )}
    </div>
  );
}
